import { useState } from "react";
import "./tablel.css";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DataType = Record<string, any>;
export type SortType = "asc" | "desc";
export type ColumnDef<T extends DataType> = {
    name: string;
    dataKey: keyof T;
    canSort?: boolean;
    defaultSort?: SortType;
    customSort?: (a: T, b: T) => number;
    render?: (rowData: T) => JSX.Element | string;
};
export type ActionButtonStyle = "default" | "red";
export type ActionDef<T> = {
    name: string;
    style?: ActionButtonStyle;
    customCssClasses?: string[];
    onClick: (rowData: T, button: HTMLButtonElement) => void;
    shouldShow?: (rowData: T) => boolean;
};

type TablelProps<T extends DataType> = {
    columnDefs: ColumnDef<T>[];
    data: T[];
    sortCol?: keyof T;
    sortDir?: SortType;
    actions?: ActionDef<T>[];
    customCssClasses?: {
        table?: string;
        thead?: string;
        th?: string;
        td?: string;
        tr?: string;
    };
};

const actionBtnStyleClasses: Record<ActionButtonStyle, string> = {
    default: "actionButtonDefault",
    red: "actionButtonRed",
};

function defaultSortFunc(val1: string | number, val2: string | number, sort: SortType): number {
    if (typeof val1 === "string") val1 = val1.toLowerCase();
    if (typeof val2 === "string") val2 = val2.toLowerCase();
    if (val1 < val2) {
        if (sort == "asc") {
            return -1;
        } else {
            return 1;
        }
    } else if (val1 > val2) {
        if (sort == "asc") {
            return 1;
        } else {
            return -1;
        }
    } else {
        return 0;
    }
}

const Tablel = <T extends DataType>({
    columnDefs,
    data,
    sortCol,
    sortDir,
    actions,
    customCssClasses,
}: TablelProps<T>): JSX.Element => {
    if (columnDefs.length === 0) return <>Table has no column definitions!</>;

    const [sortData, setSortData] = useState<{ col: keyof T; dir: SortType }>({
        col: sortCol ?? columnDefs[0]!.dataKey,
        dir: sortDir ?? "asc",
    });
    const headers: JSX.Element[] = [];
    const rows: JSX.Element[] = [];

    const changeSort = (col: string) => {
        if (sortData.col == col) {
            setSortData({ col, dir: sortData.dir == "asc" ? "desc" : "asc" });
        } else {
            setSortData({ col, dir: "asc" });
        }
    };

    const cssc = {
        table: customCssClasses?.table ?? "table",
        thead: customCssClasses?.thead,
        th: customCssClasses?.th ?? "tableTh",
        td: customCssClasses?.td ?? "tableTd",
        tr: customCssClasses?.tr ?? "tableTr",
    };

    for (const [i, columnData] of columnDefs.entries()) {
        headers.push(
            <th
                key={(columnData.dataKey as string) + i}
                className={`${cssc.th}${columnData.canSort ? " tableThSortable" : ""}`}
                onClick={columnData.canSort ? () => changeSort(columnData.dataKey as string) : undefined}
            >
                {columnData.name}
            </th>
        );
    }

    if (actions && actions.length > 0) {
        headers.push(
            <th key="actions" className={cssc.th}>
                Actions
            </th>
        );
    }

    data.sort((a, b) => defaultSortFunc(a[sortData.col], b[sortData.col], sortData.dir));

    let listKey = 1;
    for (const rowData of data) {
        const tds: JSX.Element[] = [];
        for (const [i, columnData] of columnDefs.entries()) {
            const data = rowData[columnData.dataKey];
            const content = columnData.render ? columnData.render(rowData) : data;
            tds.push(
                <td key={(columnData.dataKey as string) + i} className={cssc.td}>
                    {content}
                </td>
            );
        }

        if (actions && actions.length > 0) {
            const actionElems: JSX.Element[] = [];
            for (const ae of actions) {
                if (!ae.shouldShow || ae.shouldShow(rowData)) {
                    const cssClasses: string[] = ["tableActionButton"];
                    if (ae.customCssClasses) {
                        cssClasses.push(...ae.customCssClasses);
                    } else {
                        cssClasses.push("tableActionButtonStyle");
                        cssClasses.push(actionBtnStyleClasses[ae.style ?? "default"]);
                    }
                    actionElems.push(
                        <button
                            key={ae.name}
                            className={cssClasses.join(" ")}
                            onClick={(event) => ae.onClick(rowData, event.target as HTMLButtonElement)}
                        >
                            {ae.name}
                        </button>
                    );
                }
            }
            tds.push(
                <td key="actions" className={`${cssc.td} tableActions`}>
                    {actionElems}
                </td>
            );
        }

        rows.push(
            <tr key={listKey++} className={cssc.tr}>
                {tds}
            </tr>
        );
    }

    return (
        <table className={cssc.table}>
            <thead className={cssc.thead}>
                <tr>{headers}</tr>
            </thead>
            <tbody>{rows}</tbody>
        </table>
    );
};

export default Tablel;
